import { useEffect, useRef, useState } from 'react'
import { X, Package, Loader2 } from 'lucide-react'
import type { WireArticleBlock } from '../../../api/types'
import { useArticleArtifacts } from '../common/ArticleArtifactsContext'
import { useVisualEngagementContext } from '../common/VisualEngagementContext'
import { emitVisualInteraction, useVisualInViewTracking } from '../common/visualCollectionTracking'
import { PortionMark } from '../common/PortionMark'
import { useVisualPackage } from '../../../hooks/useVisualPackage'

type Block = Extract<WireArticleBlock, { type: 'visual' }>

export function VisualBlock({ block }: { block: Block }) {
  const artifacts = useArticleArtifacts()
  const engagement = useVisualEngagementContext()
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const figureRef = useRef<HTMLElement>(null)
  const wasLightboxOpenRef = useRef(false)

  const artifact = artifacts.find((a) => a.id === block.artifactId)
  const presignedUrl = artifact?.presignedUrl ?? null

  const { data, isLoading, error } = useVisualPackage(
    block.artifactId ?? null,
    presignedUrl,
  )

  // Effective values: block override → parsed metadata → sensible default
  const effectiveDisplay =
    block.displayOverride ?? data?.metadata.target ?? block.target ?? 'inline'
  const effectiveWidth =
    block.inlineWidthOverride ?? data?.metadata.inlineWidth ?? '100%'
  const effectiveHeight =
    block.inlineHeightOverride ?? data?.metadata.inlineHeight ?? null
  const effectiveAspectRatio =
    block.aspectRatioOverride ?? data?.metadata.aspectRatio ?? null
  const effectiveFloat =
    block.floatOverride ?? data?.metadata.float ?? 'none'
  const effectiveTargetWidth =
    block.targetWidthOverride ?? data?.metadata.targetWidth ?? '80vw'
  const effectiveTargetHeight =
    block.targetHeightOverride ?? data?.metadata.targetHeight ?? '80vh'
  const isInteractive =
    block.visualType === 'interactive' ||
    data?.metadata.type === 'interactive'
  useVisualInViewTracking(engagement, figureRef, block.artifactId, 'LEGACY_VISUAL')

  useEffect(() => {
    if (!engagement?.enabled) return
    if (lightboxOpen && !wasLightboxOpenRef.current) {
      emitVisualInteraction(engagement, block.artifactId, 'LEGACY_VISUAL', 'LIGHTBOX_OPEN')
    }
    if (!lightboxOpen && wasLightboxOpenRef.current) {
      emitVisualInteraction(engagement, block.artifactId, 'LEGACY_VISUAL', 'LIGHTBOX_CLOSE')
    }
    wasLightboxOpenRef.current = lightboxOpen
  }, [block.artifactId, engagement, lightboxOpen])

  // Float → wrapper classes
  const floatClass =
    effectiveFloat === 'left'
      ? 'float-left mr-6 mb-4 clear-left'
      : effectiveFloat === 'right'
        ? 'float-right ml-6 mb-4 clear-right'
        : ''

  // Inline sizing style — when floated, honor the metadata width (e.g. "265px"), not a hardcoded 38%
  const inlineStyle: React.CSSProperties = {
    width: effectiveWidth,
    ...(effectiveAspectRatio ? { aspectRatio: String(effectiveAspectRatio) } : {}),
    ...(effectiveHeight && !effectiveAspectRatio ? { height: effectiveHeight } : {}),
  }

  // Loading state
  if (!block.artifactId) {
    return (
      <div className="flex items-center justify-center rounded border border-dashed border-line bg-fill px-6 py-8 text-center">
        <div className="space-y-1">
          <Package size={20} className="mx-auto text-fg-secondary opacity-50" />
          <p className="text-xs text-fg-secondary opacity-60">No visual linked</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={`${floatClass}`} style={{ width: effectiveWidth }}>
        <div
          className="flex items-center justify-center rounded bg-fill"
          style={inlineStyle}
        >
          <Loader2 size={20} className="animate-spin text-fg-secondary" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="rounded border border-dashed border-red-500/40 bg-fill px-4 py-3 text-xs text-fg-secondary">
        <span className="font-medium">Visual load error</span>
        {error && <span className="ml-1 opacity-70">— {error}</span>}
      </div>
    )
  }

  const { thumbnailUrl, contentUrl, printUrl } = data.urls

  const handlePointerEnter = () => {
    emitVisualInteraction(engagement, block.artifactId, 'LEGACY_VISUAL', 'HOVER_OVER')
  }

  const handlePrimaryClick = () => {
    emitVisualInteraction(engagement, block.artifactId, 'LEGACY_VISUAL', 'CLICK')
  }

  // Lightbox content
  const lightboxInner = isInteractive && contentUrl ? (
    <iframe
      ref={iframeRef}
      src={contentUrl}
      title={block.title ?? block.jobNumber ?? 'Interactive visual'}
      className="rounded border-0"
      style={{ width: effectiveTargetWidth, height: effectiveTargetHeight }}
      sandbox="allow-scripts allow-same-origin allow-forms"
      onClick={(e) => e.stopPropagation()}
    />
  ) : (
    <img
      src={thumbnailUrl}
      alt={block.altText ?? block.title ?? ''}
      className="max-h-[85vh] max-w-[85vw] rounded object-contain"
      onClick={(e) => e.stopPropagation()}
    />
  )

  // Inline content
  const inlineContent =
    effectiveDisplay === 'popup' ? (
      // Popup mode: interactive → lightbox iframe; PDF-only → new tab; image-only → lightbox thumbnail
      <button
        type="button"
        onClick={() => {
          handlePrimaryClick()
          if (isInteractive && contentUrl) {
            setLightboxOpen(true)
          } else if (printUrl) {
            window.open(printUrl, '_blank', 'noopener,noreferrer')
          } else {
            setLightboxOpen(true)
          }
        }}
        onPointerEnter={handlePointerEnter}
        className="block w-full cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary-solid"
        aria-label={isInteractive ? 'Open interactive visual' : printUrl ? 'Open PDF' : 'Open visual'}
      >
        <img
          src={thumbnailUrl}
          alt={block.altText ?? block.title ?? ''}
          className="w-full rounded object-contain"
          style={inlineStyle}
        />
      </button>
    ) : isInteractive && contentUrl ? (
      // Interactive inline: render iframe directly
      <iframe
        ref={iframeRef}
        src={contentUrl}
        title={block.title ?? block.jobNumber ?? 'Interactive visual'}
        className="w-full rounded border-0"
        style={inlineStyle}
        sandbox="allow-scripts allow-same-origin allow-forms"
        onPointerEnter={handlePointerEnter}
      />
    ) : (
      // Static inline: show image, click opens lightbox
      <button
        type="button"
        onClick={() => {
          handlePrimaryClick()
          setLightboxOpen(true)
        }}
        onPointerEnter={handlePointerEnter}
        className="block w-full cursor-zoom-in rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary-solid"
        aria-label="Expand visual"
      >
        <img
          src={thumbnailUrl}
          alt={block.altText ?? block.title ?? ''}
          className="w-full rounded object-contain"
          style={inlineStyle}
        />
      </button>
    )

  const hasCaption = block.title || block.caption || block.sourcingLine

  return (
    <>
      <figure
        ref={figureRef}
        className={`my-0 ${floatClass}${effectiveFloat === 'none' ? ' mx-auto' : ''}`}
        style={{ width: effectiveWidth }}
      >
        <div className="relative">
          {inlineContent}
        </div>
        {hasCaption && (
          <figcaption className="mt-1.5 space-y-0.5 text-[11px] text-fg-secondary">
            {block.title && (
              <span className="block font-medium">{block.title}</span>
            )}
            {block.caption && (
              <span className="block">
                {block.captionClassification && (
                  <PortionMark classification={block.captionClassification} disseminationControls={block.captionDisseminationControls} />
                )}
                {block.caption}
              </span>
            )}
            {block.sourcingLine && (
              <span className="block italic opacity-70">{block.sourcingLine}</span>
            )}
          </figcaption>
        )}
        <div className="mt-1">
          <PortionMark
            classification={block.visualClassification ?? block.classification}
            disseminationControls={block.visualClassification ? block.visualDisseminationControls : block.disseminationControls}
          />
        </div>
      </figure>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          {lightboxInner}
          {hasCaption && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded bg-black/60 px-4 py-2 text-center text-xs text-white">
              {block.title && <p className="font-medium">{block.title}</p>}
              {block.caption && (
                <p>
                  {block.captionClassification && (
                    <PortionMark classification={block.captionClassification} disseminationControls={block.captionDisseminationControls} />
                  )}
                  {block.caption}
                </p>
              )}
              {block.sourcingLine && (
                <p className="mt-0.5 italic opacity-70">{block.sourcingLine}</p>
              )}
            </div>
          )}
        </div>
      )}
    </>
  )
}
